import { Component, ElementRef, EventEmitter, Input, OnInit, Output, forwardRef, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';

import { SsrService } from '@services/ssr.service';

@Component({
  selector: 'app-otp-input',
  imports: [NgClass, ReactiveFormsModule],
  templateUrl: './otp-input.component.html',
  styleUrl: './otp-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => OtpInputComponent),
    },
  ],
})
export class OtpInputComponent implements ControlValueAccessor, OnInit {
  private el = inject(ElementRef);
  private ssrService = inject(SsrService);

  public form = new FormGroup({
    firstNumber: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern('^.{1}$')] }),
    secondNumber: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern('^.{1}$')] }),
    thirdNumber: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern('^.{1}$')] }),
    fourthNumber: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern('^.{1}$')] }),
  });
  public onChange = (value: string) => {};
  public onTouched = (value: boolean) => {};
  @Input() disabled: boolean = false;
  @Output() done: EventEmitter<void> = new EventEmitter<void>();

  valueChanded(): void {
    const value: string = this.form.controls['firstNumber'].value + this.form.controls['secondNumber'].value + this.form.controls['thirdNumber'].value + this.form.controls['fourthNumber'].value;
    this.onChange(value);
  }

  inputKey(event: Event, name: string): void {
    if (this.ssrService.isServer()) return;
    this.onTouched(true);
    const target: HTMLInputElement | null = event.target as HTMLInputElement | null;
    if (!target) return;
    let value: string = target.value;

    if (value.length > 1) value = value.slice(-1);

    const regex = /^[0-9]*$/;
    if (!regex.test(value)) {
      value = value.replace(/[^0-9]/g, '');
      target.value = value;
      event.preventDefault();
      return;
    }

    target.value = value;

    (this.form.controls as { [key: string]: FormControl<string> })[name]?.setValue(value);

    this.valueChanded();

    let nextInput: HTMLInputElement | null = null;
    if (value.length > 0) nextInput = target.nextElementSibling as HTMLInputElement | null;
    else nextInput = target.previousElementSibling as HTMLInputElement | null;

    if (nextInput) nextInput.focus();

    if (this.form.valid) this.done.emit();
  }

  focus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
    const input = event.target as HTMLInputElement;
    setTimeout(() => {
      input.selectionStart = input.selectionEnd = 10000;
      (event.target as HTMLInputElement).select();
    }, 0);
  }

  keydown(event: KeyboardEvent): void {
    const key = event.code || event.key;
    if (key === 'ArrowLeft') event.preventDefault();
  }

  keyup(event: KeyboardEvent): void {
    const key = event.code || event.key;
    if (key === 'Backspace' || key === 'Delete') {
      const target: HTMLInputElement | null = event.target as HTMLInputElement | null;
      if (!target) return;
      const value: string = target.value;
      if (value.length === 0) {
        const previousInput = target.previousElementSibling as HTMLInputElement | null;
        if (previousInput) previousInput.focus();
      }
    }
  }

  paste(event: ClipboardEvent): void {
    let clipboardData = event.clipboardData;
    if (!clipboardData) return;
    let pastedText = clipboardData.getData('text');
    pastedText = pastedText.replace(/[^0-9]/g, '');
    const code: string[] = pastedText.split('');
    this.form.controls['firstNumber'].setValue(code[0]);
    this.form.controls['firstNumber'].updateValueAndValidity();
    this.form.controls['secondNumber'].setValue(code[1]);
    this.form.controls['secondNumber'].updateValueAndValidity();
    this.form.controls['thirdNumber'].setValue(code[2]);
    this.form.controls['thirdNumber'].updateValueAndValidity();
    this.form.controls['fourthNumber'].setValue(code[3]);
    this.form.controls['fourthNumber'].updateValueAndValidity();

    this.valueChanded();

    const index: number = code.length > 3 ? 4 : code.length + 1;
    const activeInput = this.el.nativeElement.querySelector(`.otp-input__item:nth-child(${index})`);
    if (activeInput) activeInput.focus();
    event.preventDefault();
  }

  writeValue(obj: any): void {
    const firstNumber: string = obj ? obj[0] : '';
    const secondNumber: string = obj ? obj[1] : '';
    const thirdNumber: string = obj ? obj[2] : '';
    const fourthNumber: string = obj ? obj[3] : '';
    this.form.controls['firstNumber'].setValue(firstNumber);
    this.form.controls['secondNumber'].setValue(secondNumber);
    this.form.controls['thirdNumber'].setValue(thirdNumber);
    this.form.controls['fourthNumber'].setValue(fourthNumber);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (value: boolean) => void): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    const firstInput: HTMLInputElement | null = this.el.nativeElement.querySelector('.otp-input__item');
    if (firstInput) firstInput.focus();
  }
}
